import { BrowserApi } from '@/core/apis/browser_api';
import { ShopConfigData } from '@/core/apis/config_api/config_api_types';
import { CURR_ENV } from '@/core/constants/appConfig/environment';
import {
  BrowserInstanceData,
  BrowserInstanceHandler,
} from '@/core/data_handlers/browser_instance';
import { IpDataHandler } from '@/core/data_handlers/ip_data';
import { MarketDataHandler } from '@/core/data_handlers/market';
import { PageDataHandler } from '@/core/data_handlers/page_data';
import { ShopConfigDataHandler } from '@/core/data_handlers/shop_config_data';
import { EDatalayerState } from '@/core/types';
import { Destinations } from '@/destinations';
import { Logger } from '@/shared/logger/logger';

import {
  TInitCallbackFunction,
  TMarketInitCallbackFunction,
} from '../../../constants/helpers_types/wtag_types';
import { PersistentDataLayer } from '../../datalayer';
import { WTagDataLayerBase } from '../../datalayer/datalayer_variables';
import { DataLayerStateHandler } from '../../datalayer/state';

/**
 * TODO: clean up the states and statehandling to reduce complexity
 * TODO: Implement a better callback system for init action so that
 * multiple functions can be run at various checkpoints
 *
 * Handle the init action
 * @param _inputConfig - The input config
 * @param initCallbackFunction - The init callback function
 * @param mktInitCallbackFunction - The market init callback function, it is called before the market data call
 * @returns - The result of the init action
 */
export async function handleInit(
  _inputConfig: ShopConfigData,
  initCallbackFunction: TInitCallbackFunction,
  mktInitCallbackFunction: TMarketInitCallbackFunction,
) {
  // Starting work
  DataLayerStateHandler.setState(EDatalayerState.pending);
  Logger.info('handleInit: Starting', { CURR_ENV });
  // TODO implement to actually use the inputConfig
  try {
    // Load shop config, IP data, and browser API data in parallel
    const [config, ipData, browserApiData] = await Promise.all([
      ShopConfigDataHandler.initOrLoad(_inputConfig),
      IpDataHandler.initOrLoad(),
      BrowserApi.get(),
    ]);

    if (!config.shopConfigData?.active) {
      Logger.info('handleInit: Tracking is not active', {
        config: JSON.stringify(config),
        CURR_ENV,
      });
      // No Tracking state
      DataLayerStateHandler.setState(EDatalayerState.notracking);
      return { success: false };
    }

    // Set page data from browser API
    PageDataHandler.setOnPageLoad(browserApiData);

    // Call market init callback if provided
    if (typeof mktInitCallbackFunction === 'function') {
      mktInitCallbackFunction(config.shopConfigData);
    }

    // Fetch Market Data
    const mktData = MarketDataHandler.getMarketData();

    // Initialize browser instance data and destinations in parallel
    const [bid] = await Promise.all([
      BrowserInstanceHandler.initOrLoadAndMaybeUpdate(
        browserApiData,
        ipData,
        mktData,
      ),
      Destinations.getInstance().init(
        config.getDestinations(),
        config,
        {} as BrowserInstanceData, // Pass empty object as BID is not used in destinations
      ),
    ]);

    DataLayerStateHandler.setState(EDatalayerState.ready);

    if (typeof initCallbackFunction === 'function') {
      initCallbackFunction(config.shopConfigData, bid.bd.aid, bid.sd.sid);
    }

    // Update the Datalayer to process any unprocessed events
    await (
      (window as any)[WTagDataLayerBase] as unknown as PersistentDataLayer
    ).processCompleteDataLayer();
    return { success: true };
  } catch (error) {
    // Failed to initialize
    DataLayerStateHandler.setState(EDatalayerState.error);
    Logger.error('handleInit: Failed to initialize', error);
    return { success: false };
  }
}
